/**
 * Shared presentation helpers for the Items / Inventory feature.
 *
 * Extracted from Inventory.tsx so the list page and the dedicated item detail
 * page (ItemDetails.tsx) render categories, values, and icons identically
 * without duplicating logic. List-only helpers (status badges, document-presence
 * indicators) intentionally stay in Inventory.tsx since the detail page reads
 * the full record instead.
 */
import { Package, Armchair, Tv, Wind, Droplet, Shield, Box } from 'lucide-react';
import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import type { Item } from '../../types/item';

type IconComponent = ComponentType<LucideProps>;

/** Map an item category to its Lucide icon, falling back to a generic box. */
export const getCategoryIcon = (category?: string | null): IconComponent => {
    const iconMap: Record<string, IconComponent> = {
        'Appliance': Package,
        'Furniture': Armchair,
        'Electronics': Tv,
        'HVAC': Wind,
        'Plumbing': Droplet,
        'Security': Shield,
        'Other': Box,
    };
    return (category && iconMap[category]) || Box;
};

// Category words that must stay all-caps rather than Title-cased (blanket
// lowercasing would render "HVAC" as "Hvac" on badges and detail labels).
const CATEGORY_ACRONYMS = new Set(['HVAC']);

/** Format a category for display (snake_case → Title Case), preserving known
 *  acronyms and defaulting to "Other". */
export const formatCategory = (category?: string | null): string => {
    if (!category) return 'Other';
    return category
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => (
            CATEGORY_ACRONYMS.has(word.toUpperCase())
                ? word.toUpperCase()
                : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ))
        .join(' ');
};

/* Returns the first defined value among estimated_value, purchase_price, value.
 * Null when all three are missing. Distinguishes "$0" (real) from "unset".
 * Use `?? ` rather than `||` so a legitimate 0 isn't treated as falsy. */
export const getItemValue = (item: Item): number | string | null =>
    item.estimated_value ?? item.purchase_price ?? item.value ?? null;

/* Numeric variant: always returns a number (0 when unset or non-numeric). */
export const getItemValueNum = (item: Item): number => {
    const v = getItemValue(item);
    return v != null ? (parseFloat(String(v)) || 0) : 0;
};
